import {
  APIDiscoverViewpointAttributes,
  APIConnectedUserScopeData,
  APIWorkspaceAttributes,
  ArdoqId,
  TypeRelationshipsResult,
  UnpersistedEntity,
  APIResourcePermissionAttributes,
  DirectedTriple,
  APIComponentAttributes,
} from '@ardoq/api-types';
import {
  Method,
  crudApi,
  fetchEntities,
  fetchJson,
  toRequestOptions,
} from './api';
import { ApiResponse } from './types';
import { omit } from 'lodash';

const fetchTypeRelationships = (
  workspaceIds: ArdoqId[]
): ApiResponse<TypeRelationshipsResult> => {
  const request = new Request(
    `/api/viewpoint/type-relationships/aggregated/`,
    toRequestOptions(Method.POST, { workspaceIds })
  );
  return fetchJson<TypeRelationshipsResult>(request);
};

const fetchByComponentType = (
  componentType: string
): ApiResponse<APIDiscoverViewpointAttributes[]> => {
  const request = new Request(
    `/api/viewpoint/by/component-type/`,
    toRequestOptions(Method.POST, { 'component-type-name': componentType })
  );
  return fetchJson<APIDiscoverViewpointAttributes[]>(request);
};

const fetchConnectedUser = () => {
  const request = new Request('/api/viewpoint/resource/user-connected');
  return fetchJson<APIConnectedUserScopeData>(request);
};

const fetchComponents = (componentIds: ArdoqId[]) => {
  const request = new Request(
    '/api/viewpoint/resource/component',
    toRequestOptions(Method.POST, { 'component-ids': componentIds })
  );
  return fetchJson<APIComponentAttributes[]>(request);
};

const copy = (viewpoint: APIDiscoverViewpointAttributes, newName: string) => {
  const freshViewpoint = omit(
    {
      ...viewpoint,
      name: newName,
      published: false,
    },
    ['_id', '_version', 'defaultForComponentTypeNames']
  );
  return viewpointApi.create(freshViewpoint);
};

type CreateDiscoverViewpointResponse = {
  viewpoint: APIDiscoverViewpointAttributes;
  permission: APIResourcePermissionAttributes;
};

const createWithPermissions = (
  viewpoint: UnpersistedEntity<APIDiscoverViewpointAttributes>
) => {
  const request = new Request(
    '/api/viewpoint/create',
    toRequestOptions(Method.POST, {
      ...viewpoint,
      'workspace-ids': viewpoint.workspaceIds,
    })
  );
  return fetchJson<CreateDiscoverViewpointResponse>(request);
};

type DescriptionType = 'description' | 'excuse';

const generateDescription = async (body: {
  name: string | null;
  description: string | null;
  viewName: string;
  paths: DirectedTriple[][];
}): ApiResponse<{
  text: string;
  type: DescriptionType;
}> => {
  const request = new Request(
    `/api/traversal/description/autocomplete`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<{
    text: string;
    type: DescriptionType;
  }>(request);
};

export const viewpointApi = {
  fetchConnectedUser,
  fetchComponents,
  fetchTypeRelationships,
  ...crudApi<APIDiscoverViewpointAttributes>('viewpoint'),
  fetchByComponentType,
  fetchRestrictedWorkspaces: fetchEntities<APIWorkspaceAttributes>(
    'viewpoint/resource/workspace'
  ),
  copy,
  createWithPermissions,
  generateDescription,
};
